import { useQuery } from '@tanstack/react-query';

import { useRequest } from '@frontend/api';
import type { QueryFunction } from '@frontend/api';

import type {
  QuoteCollaborationSummaryData,
  QuoteCollaborationSummaryParams,
  QuoteCollaborationSummaryQueryKey,
} from './types';

const getQuoteCollaborationSummaryQueryKey = (
  params: QuoteCollaborationSummaryParams,
): QuoteCollaborationSummaryQueryKey => ['quoteCollaborationSummary', params];

/**
 * Hook to fetch collaboration summary data for a quote, including unresolved tasks and unread annotations
 */
export const useQuoteCollaborationSummary: QueryFunction<
  QuoteCollaborationSummaryData,
  [QuoteCollaborationSummaryParams],
  QuoteCollaborationSummaryQueryKey
> = (params, { requestOptions, queryOptions } = {}) => {
  const queryKey = getQuoteCollaborationSummaryQueryKey(params);
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey,
    enabled:
      (Boolean(params.quoteNumberWithRevision) || Boolean(params.quoteId)) &&
      Boolean(queryOptions?.enabled ?? true),
    queryFn: ({ signal }) => {
      if (
        !(Boolean(params.quoteNumberWithRevision) || Boolean(params.quoteId))
      ) {
        throw new Error(
          'Either quoteNumberWithRevision or quoteId must be provided',
        );
      }

      const query: Record<string, string | number> = {};
      // Prioritize quoteNumberWithRevision since it's faster and available on mount
      if (params.quoteNumberWithRevision) {
        query.quote_number_with_revision = params.quoteNumberWithRevision;
        // Only include quoteId as fallback if quoteNumberWithRevision is not available
      } else if (params.quoteId) {
        query.quote_id = params.quoteId;
      }

      return request('/part_library/collaboration_summary/', {
        init: {
          method: 'GET',
          signal,
        },
        query,
        shouldConvertQueryKeysToSnakeCase: false, // Keys are already in snake_case
      });
    },
  });
};
